import React, { useState, useRef, useEffect } from 'react';
import { Typography, Box, Paper, Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, TextField, IconButton, Breadcrumbs, Link } from '@mui/material';
import LoadingOverlay from '../common/LoadingOverlay';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client/react';
import { SAVE_PROJECT, GET_COMPANIES, DELETE_PROJECT, GET_ELEMENT, DELETE_ATTACHMENT } from '../../graphql/projects';
import { DELETE_TASK } from '../../graphql/tasks';
import ProjectModal from './ProjectModal';
import ProjectTree from './ProjectTree';
import ShipDiagram from './ShipDiagram';
import ProjectInfo from './ProjectInfo';
import ProjectAttachments from './ProjectAttachments';
import UnitTasksList from './ElementTasksList';
import TaskProgressModal from '../tasks/TaskProgressModal';
import { Edit, Check, Close } from '@mui/icons-material';
import { UploadFiles } from '../../utils/uploads';

const ProjectDetails = () => {
  const { id, elementId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const projectTreeRef = useRef(null);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [selectedUnitId, setSelectedUnitId] = useState(elementId ? parseInt(elementId) : null);
  const [unitLabelToIdMap, setUnitLabelToIdMap] = useState({});
  const [unitIdToLabelMap, setUnitIdToLabelMap] = useState({});

  const handleUnitSelect = (label, elementIdParam) => {
    setSelectedUnit(label);
    const elementIdToUse = elementIdParam || unitLabelToIdMap[label];
    setSelectedUnitId(elementIdToUse);
    
    if (elementIdToUse) {
      navigate(`/projects/${id}/${elementIdToUse}`, { replace: true });
    }
  };

  useEffect(() => {
    if (elementId && unitIdToLabelMap[elementId] && !selectedUnit) {
      const label = unitIdToLabelMap[elementId];
      setSelectedUnit(label);
      setSelectedUnitId(parseInt(elementId));
    }
  }, [elementId, unitIdToLabelMap, selectedUnit]);
  const { data: projectQueryData, refetch: refetchProject } = useQuery(GET_ELEMENT, {
    variables: { id },
    skip: !id
  });
  const projectData = projectQueryData?.element || location.state?.project;
  const [modalOpen, setModalOpen] = useState(false);
  const [editingElement, setEditingElement] = useState(null);
  const [parentElement, setParentElement] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [elementToDelete, setElementToDelete] = useState(null);
  const [attachmentToDelete, setAttachmentToDelete] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [editDates, setEditDates] = useState({ start: '', end: '' });
  const fileInputRef = useRef(null);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [taskDeleteDialogOpen, setTaskDeleteDialogOpen] = useState(false);
  const [commentsModalOpen, setCommentsModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);

  const { data: companiesData } = useQuery(GET_COMPANIES);
  const [saveProject] = useMutation(SAVE_PROJECT);
  const [deleteProject] = useMutation(DELETE_PROJECT);
  const [deleteAttachment] = useMutation(DELETE_ATTACHMENT);
  const [deleteTask] = useMutation(DELETE_TASK);
  const getCompanyNames = (companyIds) => {
    if (!companyIds || companyIds.length === 0 || !companiesData?.companies?.list) return '';
    return companyIds
      .map(id => companiesData.companies.list.find(c => c.id === id)?.name)
      .filter(Boolean)
      .join(', ');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    let date;
    if (dateString.includes('-') && dateString.split('-').length === 3) {
      const [day, month, year] = dateString.split('-');
      date = new Date(year, month - 1, day);
    } else {
      date = new Date(dateString);
    }
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };



  const handleSaveElement = async (elementData) => {
    try {
      const attributes = {
        ...elementData,
        parent_id: parentElement ? parseInt(parentElement.id) : parseInt(id)
      };
      if (editingElement) {
        attributes.id = parseInt(editingElement.id);
      }
      const result = await saveProject({ variables: { attributes } });
      return result.data.createOrUpdateProjects.project;
    } catch (error) {
      throw error;
    }
  };

  const handleUploadComplete = async () => {
    setRefreshing(true);
    setRefreshing(false);
  };



  const confirmDelete = async () => {
    try {
      await deleteProject({ variables: { id: elementToDelete.id.toString() } });
      await handleUploadComplete();
    } catch (error) {
      // Handle error silently
    } finally {
      setDeleteDialogOpen(false);
      setElementToDelete(null);
    }
  };

  const confirmDeleteAttachment = async () => {
    try {
      await deleteAttachment({
        variables: {
          attachment_id: [parseInt(attachmentToDelete.id)],
          attachable_id: parseInt(id),
          attachable_type: 'WlWelds::Project'
        }
      });
      await refetchProject();
    } catch (error) {
      // Handle error silently
    } finally {
      setAttachmentToDelete(null);
    }
  };

  const handleEditField = (field, value) => {
    setEditingField(field);
    if (field === 'dates') {
      const convertToInputDate = (dateString) => {
        if (!dateString) return '';
        if (dateString.includes('-') && dateString.split('-').length === 3) {
          const [day, month, year] = dateString.split('-');
          return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
        return dateString;
      };
      setEditDates({
        start: convertToInputDate(projectData.planned_start_date),
        end: convertToInputDate(projectData.planned_end_date)
      });
    } else if (field === 'company_ids') {
      setEditValue(value || []);
    } else {
      setEditValue(value || '');
    }
  };

  const handleSaveField = async () => {
    try {
      const attributes = { id: parseInt(id) };
      
      if (editingField === 'dates') {
        const convertToApiDate = (dateString) => {
          if (!dateString) return '';
          const [year, month, day] = dateString.split('-');
          return `${day}-${month}-${year}`;
        };
        attributes.planned_start_date = convertToApiDate(editDates.start);
        attributes.planned_end_date = convertToApiDate(editDates.end);
      } else {
        attributes[editingField] = editValue;
      }
      
      await saveProject({ variables: { attributes } });
      await refetchProject();
      setEditingField(null);
      setEditValue('');
      setEditDates({ start: '', end: '' });
    } catch (error) {
      // Handle error silently
    }
  };

  const handleCancelEdit = () => {
    setEditingField(null);
    setEditValue('');
    setEditDates({ start: '', end: '' });
  };

  const handleFileSelect = async (files) => {
    const validFormats = ['png', 'jpg', 'jpeg', 'pdf', 'dwg', 'svg'];
    const newFiles = Array.from(files).filter(file => {
      const extension = file.name.split('.').pop().toLowerCase();
      return validFormats.includes(extension);
    });

    if (newFiles.length > 0) {
      try {
        await UploadFiles(parseInt(id), 'WlWelds::Project', newFiles);
        await refetchProject();
      } catch (error) {
        // Handle error silently
      }
    }
  };

  const handleDeleteAttachment = (attachment) => {
    setAttachmentToDelete(attachment);
  };

  const handleDeleteTask = (task, onSuccess) => {
    setTaskToDelete({ task, onSuccess });
    setTaskDeleteDialogOpen(true);
  };

  const confirmDeleteTask = async () => {
    if (!taskToDelete) return;
    try {
      await deleteTask({ variables: { id: taskToDelete.task.id.toString() } });
      if (taskToDelete.onSuccess) {
        taskToDelete.onSuccess();
      }
    } catch (error) {
      // Handle error silently
    } finally {
      setTaskDeleteDialogOpen(false);
      setTaskToDelete(null);
    }
  };

  const handleTaskComment = (task) => {
    setSelectedTask(task);
    setCommentsModalOpen(true);
  };

  if (refreshing) return <LoadingOverlay />;
  
  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumbs */}
      <Box sx={{ mb: 2 }}>
        <Breadcrumbs>
          <Link 
            component="button" 
            variant="body1" 
            onClick={() => navigate('/projects')}
            sx={{ cursor: 'pointer', textDecoration: 'none' }}
          >
            Projects
          </Link>
          <Typography color="text.primary">{projectData?.name || 'Project'}</Typography>
        </Breadcrumbs>
      </Box>

      {/* Project Header */}
      {projectData && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            {editingField === 'name' ? (
              <>
                <TextField
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  variant="standard"
                  sx={{ fontSize: '2rem', fontWeight: 600 }}
                  InputProps={{ style: { fontSize: '2rem', fontWeight: 600 } }}
                />
                <IconButton onClick={handleSaveField} color="success" size="small" sx={{ ml: 1 }}>
                  <Check />
                </IconButton>
                <IconButton onClick={handleCancelEdit} color="error" size="small">
                  <Close />
                </IconButton>
              </>
            ) : (
              <>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
                  {projectData.name}{projectData.number ? ` (${projectData.number})` : ''}
                </Typography>
                <IconButton onClick={() => handleEditField('name', projectData.name)} size="small" sx={{ ml: 1 }}>
                  <Edit fontSize="small" />
                </IconButton>
              </>
            )}
          </Box>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 3 }}>
            <ProjectInfo
              projectData={projectData}
              editingField={editingField}
              editValue={editValue}
              editDates={editDates}
              companiesData={companiesData}
              getCompanyNames={getCompanyNames}
              formatDate={formatDate}
              onEditField={handleEditField}
              onSaveField={handleSaveField}
              onCancelEdit={handleCancelEdit}
              onEditValueChange={setEditValue}
              onEditDatesChange={setEditDates}
            />
            <ProjectAttachments
              projectData={projectData}
              fileInputRef={fileInputRef}
              onFileSelect={handleFileSelect}
              onDeleteAttachment={handleDeleteAttachment}
            />
          </Box>
        </Paper>
      )}
      <Box sx={{ display: 'flex', gap: 3, width: '100%' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <ShipDiagram 
            project={projectData} 
            formIsDisabled={false}
            onImageSaved={async () => {
              await refetchProject();
              if (projectTreeRef.current?.refetch) {
                await projectTreeRef.current.refetch();
              }
            }}
            selectedUnit={selectedUnit}
            onUnitSelect={handleUnitSelect}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <ProjectTree 
            parentId={parseInt(id)} 
            ref={projectTreeRef} 
            selectedUnit={selectedUnit} 
            onUnitSelect={handleUnitSelect}
            onTreeDataChange={(labelToId, idToLabel) => {
              setUnitLabelToIdMap(labelToId);
              setUnitIdToLabelMap(idToLabel);
            }}
          />
        </Box>
      </Box>
      
      <UnitTasksList 
        projectId={id} 
        unitId={selectedUnitId}
        unitName={selectedUnit}
        onDelete={handleDeleteTask}
        onComment={handleTaskComment}
        key={selectedUnitId}
      />
      <ProjectModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingElement(null);
          setParentElement(null);
        }}
        project={editingElement}
        parentId={parentElement ? parseInt(parentElement.id) : parseInt(id)}
        companies={companiesData?.companies?.list || []}
        onSave={handleSaveElement}
        onUploadComplete={handleUploadComplete}
        loadingText="Saving element..."
      />
      
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Element</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{elementToDelete?.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!attachmentToDelete}
        onClose={() => setAttachmentToDelete(null)}
      >
        <DialogTitle>Delete Attachment</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{attachmentToDelete?.data_file_name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAttachmentToDelete(null)}>Cancel</Button>
          <Button onClick={confirmDeleteAttachment} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={taskDeleteDialogOpen}
        onClose={() => setTaskDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Task</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{taskToDelete?.task?.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTaskDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDeleteTask} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      <TaskProgressModal
        open={commentsModalOpen}
        onClose={() => setCommentsModalOpen(false)}
        taskId={selectedTask?.id}
        task={selectedTask}
      />
    </Box>
  );
};

export default ProjectDetails;